import React from 'react';
import useGameStore from '../../game/gameStore';
import { getOffice, PROBLEM_TYPES } from '../../game/gameStore';

export default function ResourcePanel({ selectedTerritory, onAllocate, onConfirmResolve }) {
  const turnPhase = useGameStore(s => s.turnPhase);
  const player = useGameStore(s => s.player);
  const currentOffice = useGameStore(s => s.currentOffice);
  const turn = useGameStore(s => s.turn);
  const maxTurns = useGameStore(s => s.maxTurns);
  const playerParty = useGameStore(s => s.playerParty);
  const mapLevel = useGameStore(s => s.mapLevel);

  const office = getOffice(currentOffice);

  return (
    <div className="flex flex-col h-full bg-[#080e1c] text-white overflow-y-auto">
      {/* Header */}
      <div className="p-3 border-b border-[#1a2848]">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-lg">{office.emoji}</span>
          <div>
            <p className="text-sm font-bold text-white">{office.name}</p>
            <p className="text-[9px] text-gray-500">{office.esfera} • {office.poder}</p>
          </div>
          <span className="ml-auto text-[10px] text-gray-500 bg-[#0f1a2e] px-2 py-0.5 rounded border border-[#1a2848]">
            {playerParty}
          </span>
        </div>
        <div className="flex justify-between text-[10px] text-gray-500 mt-1">
          <span>Turno {turn}/{maxTurns}</span>
          <span className={turnPhase === 'allocate' ? 'text-yellow-400 font-bold' : ''}>
            {turnPhase === 'event' ? '📋 Evento' : turnPhase === 'allocate' ? '🗺️ Alocação' : '⚡ Resolução'}
          </span>
        </div>
      </div>

      {/* Dual Charisma */}
      <div className="p-3 border-b border-[#1a2848]">
        <p className="text-[9px] text-gray-600 uppercase tracking-wider font-bold mb-2">Carisma</p>
        <div className="space-y-1.5">
          <DualBar emoji="🏛️" label="Vereador" value={player.vereadorCarisma || 0} max={100} color="#a855f7" />
          <DualBar emoji="📎" label="Você (Assessor)" value={player.popularity} max={100} color="#4ade80" />
        </div>
      </div>

      {/* Player Stats */}
      <div className="p-3 border-b border-[#1a2848]">
        <p className="text-[9px] text-gray-600 uppercase tracking-wider font-bold mb-2">Status</p>
        <div className="grid grid-cols-2 gap-1.5">
          <StatChip emoji="⭐" label="Influência" value={player.influence} max={100} color="#60a5fa" />
          <StatChip emoji="💰" label="Recursos" value={player.money} color="#fbbf24" />
          <StatChip emoji="⚠️" label="Risco" value={player.risk} max={100} color="#f97316" danger={player.risk >= 60} />
        </div>
      </div>

      {/* Problem legend */}
      {mapLevel === 'municipio' && (
        <div className="p-3 border-b border-[#1a2848]">
          <p className="text-[9px] text-gray-600 uppercase tracking-wider font-bold mb-2">Áreas de Atuação</p>
          <div className="grid grid-cols-2 gap-1">
            {Object.values(PROBLEM_TYPES).map(p => (
              <div key={p.id} className="flex items-center gap-1.5 py-0.5">
                <span className="text-[10px]">{p.emoji}</span>
                <span className="text-[9px] text-gray-400">{p.label}</span>
              </div>
            ))}
          </div>
          <p className="text-[8px] text-gray-600 mt-2 italic">
            Selecione um bairro na grid para investir
          </p>
        </div>
      )}

      {/* Resolve button */}
      {turnPhase === 'allocate' && (
        <div className="p-3 mt-auto">
          <button
            onClick={onConfirmResolve}
            className="w-full py-2.5 rounded-lg font-bold text-sm bg-gradient-to-r from-[#1a5c3a] to-[#0a3c2a] border border-[#2a7c4a] text-green-300 hover:from-[#2a6c4a] hover:to-[#1a4c3a] transition-all"
          >
            ⚡ Resolver Turno
          </button>
        </div>
      )}

      {turnPhase === 'event' && (
        <div className="p-3 flex-1 flex items-center justify-center">
          <p className="text-gray-600 text-[10px] text-center italic">
            Aguardando evento...
          </p>
        </div>
      )}
    </div>
  );
}

function DualBar({ emoji, label, value, max, color }) {
  const pct = max ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="bg-[#0a1424] rounded-lg px-2.5 py-2 border border-[#1a2848]">
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-sm">{emoji}</span>
        <span className="text-[10px] text-gray-400 flex-1">{label}</span>
        <span className="text-[11px] font-bold" style={{ color }}>{value}%</span>
      </div>
      <div className="w-full h-2 bg-[#0f1a2e] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

function StatChip({ emoji, label, value, max, color, danger }) {
  const pct = max ? Math.min(100, (value / max) * 100) : null;
  return (
    <div className={`bg-[#0a1424] rounded px-2 py-1.5 border ${danger ? 'border-red-900/50' : 'border-[#1a2848]'}`}>
      <div className="flex items-center gap-1 mb-0.5">
        <span className="text-[10px]">{emoji}</span>
        <span className="text-[9px] text-gray-500">{label}</span>
        <span className="text-[10px] font-bold text-white ml-auto">{value}{max ? '%' : ''}</span>
      </div>
      {pct !== null && (
        <div className="w-full h-1 bg-[#0f1a2e] rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-300" style={{ width: `${pct}%`, backgroundColor: color }} />
        </div>
      )}
    </div>
  );
}
